import { FormikHelpers, FormikValues } from 'formik';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { object, string } from 'yup';

import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Buscando from 'Component/Buscando';
import Cabecalho from 'Component/Cabecalho';
import Form, { Campo, Selecao } from 'Component/Form';
import LayoutDefault from 'Component/Layout';
import Paginacao from 'Component/Paginacao';
import SemRegistros from 'Component/SemRegistros';
import { TreinamentoLista } from 'Component/Treinamento';
import Parametros from 'Interface/Requisicao';
import { useAppSelector } from 'Store';
import { useRecuperarDepartamentosQuery } from 'View/Service/Default/Departamentos';
import { useRecuperarTreinamentosQuery } from 'View/Service/Default/Treinamentos';

const regras = object({
  busca: string().optional().min(3, 'Deve ter mais que 3 letras'),
});

const Default = (): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();

  const usuario = useAppSelector((estado) => estado.autenticacao.usuario);

  const { data: { departamentos } = {} } = useRecuperarDepartamentosQuery({
    finder: 'listaJson',
    limit: 200,
    sort: 'nome',
  });

  const {
    data: { treinamentos, paginacao } = {},
    refetch,
    isFetching,
    isLoading,
  } = useRecuperarTreinamentosQuery({
    contain: ['Departamentos'],
    page: searchParams.get('pagina') || 1,
    options: {
      busca: { termo: searchParams.get('busca') || '' },
      departamento_id: searchParams.get('departamento_id') || '',
    },
  } as Parametros);

  const filtrar = (
    dados: { busca: string; departamento_id: string },
    form: FormikHelpers<FormikValues>
  ) => {
    if (dados.departamento_id) {
      searchParams.set('departamento_id', dados.departamento_id);
    } else {
      searchParams.delete('departamento_id');
    }
    searchParams.set('busca', dados.busca);
    searchParams.set('pagina', '1');

    setSearchParams(searchParams);

    form.setSubmitting(false);
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Treinamentos"
        descricao="Listagem de treinamentos cadastrados no banco de dados"
      />
      <Form
        emLinha
        onSubmit={filtrar}
        regras={regras}
        desabilitado={isFetching}
      >
        <div className="row">
          <div className="col-md-6 mb-3">
            <Botao icone="arrow-clockwise" onClick={refetch} className="me-2" />
            {['super', 'admin'].includes(usuario?.permissao || '') && (
              <BotaoLink
                tema="success"
                icone="plus-lg"
                to="/treinamentos/adicionar"
              >
                Adicionar
              </BotaoLink>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <div className="hstack gap-3">
              <Selecao
                name="departamento_id"
                opcoes={departamentos}
                padrao="Departamento"
              />
              <Campo name="busca" rotulo="Digite o que deseja buscar" />
              <Botao type="submit" icone="filter" />
            </div>
          </div>
        </div>
      </Form>
      <Buscando exibir={isLoading} />
      <SemRegistros
        carregando={isLoading}
        registros={treinamentos?.length}
        redirecionar={
          ['super', 'admin'].includes(usuario?.permissao || '')
            ? '/treinamentos/adicionar'
            : undefined
        }
      />
      <div className="bg-white shadow-sm rounded">
        {treinamentos?.map((treinamento) => (
          <TreinamentoLista
            key={`${treinamento.id}-${treinamento.modificado}`}
            dados={treinamento}
          />
        ))}
      </div>
      <Paginacao dados={paginacao} desabilitado={isFetching} />
    </LayoutDefault>
  );
};

export default Default;
